import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Rocket } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/terminal/Header";
import RecommendedIndicators from "@/components/terminal/RecommendedIndicators";
import UploadZone from "@/components/terminal/UploadZone";
import LoadingOverlay from "@/components/terminal/LoadingOverlay";
import AnalysisResult from "@/components/terminal/AnalysisResult";
import PerformancePanel from "@/components/terminal/PerformancePanel";
import AutoSignalsPanel from "@/components/terminal/AutoSignalsPanel";
import PositionsPanel from "@/components/terminal/PositionsPanel";
import { AccountPanel } from "@/components/terminal/AccountPanel";
import { AdminUsersPanel } from "@/components/terminal/AdminUsersPanel";
import { CreditsPanel } from "@/components/terminal/CreditsPanel";
import { AdminPaymentsPanel } from "@/components/terminal/AdminPaymentsPanel";
import { UserPositionsPanel } from "@/components/terminal/UserPositionsPanel";
import { ReferralPanel } from "@/components/terminal/ReferralPanel";
import { AdminWithdrawalsPanel } from "@/components/terminal/AdminWithdrawalsPanel";
import { TelegramPopup, TelegramNotice } from "@/components/terminal/TelegramJoin";

export default function Terminal() {
  const { user } = useAuth();
  const [images, setImages] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [sending, setSending] = useState(false);
  const [stats, setStats] = useState(null);
  const [tg, setTg] = useState({ configured: false, is_admin: false });
  const [autoSignals, setAutoSignals] = useState([]);
  const [scanning, setScanning] = useState(false);
  const resultRef = useRef(null);

  const isAdmin = user?.role === "admin";

  const loadData = async () => {
    try {
      const reqs = [api.get("/stats")];
      if (isAdmin) reqs.push(api.get("/auto-signals"));
      const res = await Promise.all(reqs);
      setStats(res[0].data);
      if (isAdmin) setAutoSignals(res[1].data);
    } catch {
      /* silent */
    }
  };

  useEffect(() => {
    loadData();
    api.get("/telegram/status").then(({ data }) => setTg(data)).catch(() => {});
    const iv = setInterval(() => {
      api.post("/track-now").catch(() => {}).finally(loadData);
    }, 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAnalyze = async () => {
    if (images.length === 0) return;
    setAnalyzing(true);
    setResult(null);
    try {
      const { data } = await api.post("/analyze", { images: images.map((i) => i.dataUrl) });
      setResult(data);
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Analiz sırasında bir hata oluştu");
    } finally {
      setAnalyzing(false);
    }
  };

  // Open the analysed trade LIVE on BingX. Admin -> all active accounts; user -> own account.
  const handleExecute = async () => {
    if (!result?.id) return;
    setSending(true);
    try {
      const { data } = await api.post(`/trades/${result.id}/execute`);
      const fail = data.failed_count ? ` · ${data.failed_count} başarısız` : "";
      toast.success(`İşlem açıldı · ${data.opened_count} hesap${fail}`);
      setResult(null);
      setImages([]);
      await loadData();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "İşlem açılamadı");
    } finally {
      setSending(false);
    }
  };

  const handleTelegram = async (id) => {
    if (!tg.configured) {
      toast.error("Telegram yapılandırılmamış. Bot Token ve Chat ID eklenmeli.");
      return;
    }
    try {
      await api.post(`/trades/${id}/telegram`);
      toast.success("Telegram kanalına gönderildi");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Telegram gönderimi başarısız");
    }
  };

  const handleScan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/scan-now");
      await loadData();
      if (data.new_signals > 0) toast.success(`${data.new_signals} yeni oto sinyal bulundu (${data.scanned} coin tarandı)`);
      else toast.info(`Tarama tamam · ${data.scanned} coin · yeni sinyal yok`);
    } catch {
      toast.error("Tarama başarısız");
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="min-h-screen grain">
      <Header />
      <TelegramPopup />
      {analyzing && <LoadingOverlay />}

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="terminal-main">
        <TelegramNotice />
        <RecommendedIndicators />
        {!isAdmin && <CreditsPanel />}
        <UploadZone images={images} setImages={setImages} onAnalyze={handleAnalyze} analyzing={analyzing} />

        <div ref={resultRef}>
          {result && (
            <div className="space-y-4">
              <AnalysisResult result={result} onTelegram={handleTelegram} telegram={tg} />
              <button
                data-testid="execute-trade-button"
                onClick={handleExecute}
                disabled={sending}
                className="w-full inline-flex items-center justify-center gap-2 text-sm font-bold px-5 py-3.5 rounded-2xl transition-all hover:brightness-110 disabled:opacity-50"
                style={{ background: "linear-gradient(135deg,#10B981,#059669)", color: "#fff" }}
              >
                <Rocket className={`w-5 h-5 ${sending ? "animate-pulse" : ""}`} />
                {sending
                  ? "BingX'te açılıyor..."
                  : isAdmin
                    ? "GÖNDER · Tüm Aktif Hesaplarda İşlem Aç"
                    : "BingX İle İşlem Aç (Kendi Hesabım)"}
              </button>
            </div>
          )}
        </div>

        <PerformancePanel stats={stats} />

        {!isAdmin && <AccountPanel />}

        {!isAdmin && <UserPositionsPanel />}

        <ReferralPanel />

        {isAdmin && (
          <AutoSignalsPanel signals={autoSignals} onScan={handleScan} scanning={scanning} />
        )}

        {isAdmin && <PositionsPanel />}

        {isAdmin && <AdminPaymentsPanel />}

        {isAdmin && <AdminWithdrawalsPanel />}

        {isAdmin && <AdminUsersPanel />}
      </main>

      <footer className="max-w-6xl mx-auto px-6 py-8 text-center text-xs" style={{ color: "var(--text-muted)" }}>
        LUNA AI · Grafik Analiz + Oto Sinyal Terminali · Yatırım tavsiyesi değildir
      </footer>
    </div>
  );
}
